<script lang="ts">
  /**
   * Somebody asked you to play: who (an avatar), what ("Sam invited you to a
   * game"), one line of detail ("Hard · 2 min ago"), and two answers — the
   * one the card is for (Join) and the one that puts it off (Later, Decline).
   *
   * Two looks:
   *
   * - **card** (the default): drawn in the accent so it reads as the newest,
   *   most actionable thing on the screen it sits at the top of. A `section`
   *   named by its title, so a screen reader moving by landmark hears who is
   *   asking.
   * - **compact** (since 1.2.0): one line on a card surface, the answers
   *   beside the words and wrapping under them on a narrow phone — what
   *   `HomeTemplate`'s `invites` stacks above New game, where a filled accent
   *   block would compete with the tile. A `group` named by its title (a
   *   list of landmarks would be noise), and the answers keep the accent:
   *   Join filled, the other an outline.
   *
   * `primaryName` / `secondaryName` are the buttons' accessible names when
   * the words alone are ambiguous ("Join" beside two other invites: "Join
   * Sam's game").
   *
   * Every word arrives as a prop, already in the game's language.
   */
  interface Props {
    /** The avatar's letter — the inviter's initial. */
    readonly initial: string;
    readonly title: string;
    readonly detail?: string | undefined;
    readonly primaryLabel: string;
    readonly onprimary: () => void;
    readonly secondaryLabel?: string | undefined;
    readonly onsecondary?: (() => void) | undefined;
    /** The primary button's accessible name, when its words need the who ("Join Sam's game"). */
    readonly primaryName?: string | undefined;
    /** The secondary button's accessible name ("Decline Sam's invite"). */
    readonly secondaryName?: string | undefined;
    /** One line on a card surface rather than an accent block (since 1.2.0). */
    readonly compact?: boolean;
    /** The primary button keeps its size and says it is working. */
    readonly busy?: boolean;
    readonly primaryTestid?: string | undefined;
    readonly secondaryTestid?: string | undefined;
    readonly testid?: string | undefined;
  }
  const {
    initial,
    title,
    detail,
    primaryLabel,
    onprimary,
    secondaryLabel,
    onsecondary,
    primaryName,
    secondaryName,
    compact = false,
    busy = false,
    primaryTestid,
    secondaryTestid,
    testid,
  }: Props = $props();

  const titleId = $props.id();

  function primary(): void {
    if (busy) return;
    onprimary();
  }
</script>

{#snippet body()}
  <div class="ui-invite-head">
    <span class="ui-invite-avatar" aria-hidden="true">{initial}</span>
    <span class="ui-invite-text">
      <b id={titleId} class="ui-invite-title">{title}</b>
      {#if detail !== undefined}
        <span class="ui-invite-detail">{detail}</span>
      {/if}
    </span>
  </div>
  <div class="ui-invite-actions">
    <button
      type="button"
      class="ui-invite-button ui-invite-button--primary"
      aria-label={primaryName}
      aria-busy={busy ? 'true' : undefined}
      data-testid={primaryTestid}
      onclick={primary}
    >
      {primaryLabel}
    </button>
    {#if secondaryLabel !== undefined && onsecondary !== undefined}
      <button
        type="button"
        class="ui-invite-button ui-invite-button--secondary"
        aria-label={secondaryName}
        data-testid={secondaryTestid}
        onclick={onsecondary}
      >
        {secondaryLabel}
      </button>
    {/if}
  </div>
{/snippet}

{#if compact}
  <div
    class="ui-invite ui-invite--compact"
    role="group"
    aria-labelledby={titleId}
    data-testid={testid}
  >
    {@render body()}
  </div>
{:else}
  <section class="ui-invite" aria-labelledby={titleId} data-testid={testid}>
    {@render body()}
  </section>
{/if}

<style>
  .ui-invite {
    display: flex;
    flex-direction: column;
    gap: var(--ui-space-3);
    padding: var(--ui-space-4);
    border-radius: var(--ui-radius-lg);
    background: var(--ui-accent);
    color: var(--ui-on-accent);
    font-family: var(--ui-font);
  }

  .ui-invite-head {
    display: flex;
    align-items: center;
    gap: var(--ui-space-3);
  }

  .ui-invite-avatar {
    flex: 0 0 auto;
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    /* The on-accent ink as a ring and a letter: no new colour on the accent. */
    border: 2px solid var(--ui-on-accent);
    color: var(--ui-on-accent);
    font-weight: 800;
  }

  .ui-invite-text {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
  }

  .ui-invite-title {
    font: var(--ui-text-body);
    font-weight: 800;
  }

  .ui-invite-detail {
    font: var(--ui-text-label);
  }

  .ui-invite-actions {
    display: flex;
    gap: var(--ui-space-2);
  }

  .ui-invite-button {
    min-height: 2.75rem;
    padding: 0.5rem 1.2rem;
    border-radius: 999px;
    font: var(--ui-text-label);
    font-weight: 800;
  }

  .ui-invite-button--primary {
    border: 0;
    background: var(--ui-on-accent);
    color: var(--ui-accent);
  }

  .ui-invite-button--secondary {
    border: 2px solid var(--ui-on-accent);
    background: transparent;
    color: var(--ui-on-accent);
  }

  .ui-invite-button:focus-visible {
    outline: 3px solid var(--ui-on-accent);
    outline-offset: 2px;
  }

  /* ── compact: one line on a card, the answers beside the words ───────── */

  .ui-invite--compact {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    column-gap: var(--ui-space-3);
    row-gap: var(--ui-space-2);
    padding: var(--ui-space-2) var(--ui-space-3);
    border: 1px solid var(--ui-line);
    border-radius: var(--ui-radius-md);
    background: var(--ui-card);
    color: var(--ui-ink);
  }

  /* The words take what is left, but never less than a short name and its
     line: below that the answers wrap under them instead. */
  .ui-invite--compact .ui-invite-head {
    flex: 1 1 9rem;
    min-width: 0;
  }

  .ui-invite--compact .ui-invite-avatar {
    width: 2.25rem;
    height: 2.25rem;
    border: 0;
    background: var(--ui-accent);
    color: var(--ui-on-accent);
    font: var(--ui-text-body);
    font-weight: 800;
  }

  .ui-invite--compact .ui-invite-title,
  .ui-invite--compact .ui-invite-detail {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .ui-invite--compact .ui-invite-detail {
    font: var(--ui-text-caption);
    color: var(--ui-ink-2);
  }

  .ui-invite--compact .ui-invite-actions {
    flex: 0 0 auto;
    margin-left: auto;
  }

  /* 2.25rem to the eye, the target still 2.75rem. */
  .ui-invite--compact .ui-invite-button {
    position: relative;
    min-height: 2.25rem;
    padding: 0.35rem 0.85rem;
  }

  .ui-invite--compact .ui-invite-button::before {
    content: '';
    position: absolute;
    inset: -0.25rem 0;
  }

  .ui-invite--compact .ui-invite-button--primary {
    background: var(--ui-accent);
    color: var(--ui-on-accent);
  }

  .ui-invite--compact .ui-invite-button--secondary {
    border-color: var(--ui-accent);
    color: var(--ui-accent);
  }

  .ui-invite--compact .ui-invite-button:focus-visible {
    outline-color: var(--ui-accent);
  }
</style>
